import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import Router from "next/router";
import { dashboardRouterEvents } from "@/hooks/useDashboardRouter";
import { navigateTo, reloadPage } from "@/lib/browserNavigation";
const POLL_INTERVAL_MS = 30 * 60 * 1000;
const STALE_CHUNK_RELOAD_KEY = "stale-chunk-reload";
const STALE_CHUNK_RELOAD_WINDOW_MS = 30 * 1000;

// The abort surfaces as an unhandled rejection that the global handler from
// lib/errorReporting forwards to Sentry; instrumentation-client.ts filters
// events matching this message in beforeSend.
export const VERSION_REFRESH_ABORT_MESSAGE = "routeChange aborted to apply new frontend version";
async function fetchDeployedSha(): Promise<string | null> {
  try {
    const res = await fetch("/api/version", {
      cache: "no-store"
    });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    const sha = (data as {
      sha?: unknown;
    }).sha;
    return typeof sha === "string" && sha.length > 0 ? sha : null;
  } catch {
    return null;
  }
}
export function isStaleChunkError(err: unknown): boolean {
  const message = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  return /ChunkLoadError|Loading chunk/i.test(message);
}

/**
 * Whether a render error is a stale chunk the boundary will recover from by
 * reloading: one attempt per URL within a short window, so a chunk that keeps
 * failing shows the error splash instead of a reload loop. Without
 * sessionStorage there is no loop guard, so no recovery either. The fallback
 * renders before `onError` runs, so it asks this first to stay blank and
 * unreported while the reload is about to happen.
 */
export function canRecoverFromStaleChunk(error: unknown): boolean {
  if (!isStaleChunkError(error)) return false;
  try {
    const previous = window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY);
    if (!previous) return true;
    const [previousUrl, at] = previous.split("\n");
    return previousUrl !== window.location.href || Date.now() - Number(at) >= STALE_CHUNK_RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
}

/**
 * App Router counterpart of the routeChangeError recovery below: a lazy
 * chunk that stops resolving after a deploy throws into the top-level error
 * boundary rather than failing a navigation, so the boundary hard-navigates
 * to the current URL.
 */
export function recoverFromStaleChunkRender(error: unknown) {
  if (!canRecoverFromStaleChunk(error)) return;
  const url = window.location.href;
  try {
    window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, `${url}\n${Date.now()}`);
  } catch {
    return;
  }
  navigateTo(url);
}

/**
 * Quietly picks up new frontend deploys in long-lived tabs. Polls
 * /api/version (which reports the latest deployment's sha) and compares it
 * against the sha baked into this bundle. Once they differ, the new version
 * is applied at the first moment the user won't notice:
 *
 * - immediately, if the tab is hidden (or as soon as it becomes hidden)
 * - otherwise on the next route change, by turning the client-side
 *   transition into a full page load
 *
 * Polls skip while offline, and tabs that a mobile browser froze or
 * discarded (no polls run while hidden there) catch up with a single check
 * when they become visible again.
 *
 * Also recovers from stale-chunk navigation failures (the old deployment's
 * lazy chunks stop resolving after a deploy) by hard-navigating instead of
 * surfacing the error splash. Only the Pages Router reports those as
 * routeChangeError; on the App Router they throw during render, see
 * `recoverFromStaleChunkRender`.
 *
 * Listens on whichever router is live: `next/router`'s events fire only from
 * the Pages Router instance, and the App Router shell announces its
 * navigations through `dashboardRouterEvents`.
 */
export function useFrontendVersionRefresh() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
}
function _temp2() {
  const builtSha = process.env.NEXT_PUBLIC_BUILD_SHA;
  if (!builtSha || builtSha === "dev") {
    return;
  }
  const pagesRouter = Boolean(Router.router);
  const events = pagesRouter ? Router.events : dashboardRouterEvents;
  let updatePending = false;
  let lastCheckAt = Date.now();
  const reloadIfHidden = () => {
    if (updatePending && document.visibilityState === "hidden") {
      reloadPage();
    }
  };
  const check = async () => {
    if (updatePending || navigator.onLine === false) {
      return;
    }
    lastCheckAt = Date.now();
    const deployedSha = await fetchDeployedSha();
    if (deployedSha && deployedSha !== builtSha) {
      updatePending = true;
      reloadIfHidden();
    }
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      reloadIfHidden();
    } else {
      if (Date.now() - lastCheckAt >= POLL_INTERVAL_MS) {
        check();
      }
    }
  };
  const applyOnNavigation = (url, options) => {
    if (!updatePending || options?.shallow) {
      return;
    }
    navigateTo(url);
    events.emit("routeChangeError", VERSION_REFRESH_ABORT_MESSAGE, url);
    if (pagesRouter) {
      throw VERSION_REFRESH_ABORT_MESSAGE;
    }
  };
  const recoverFromStaleChunk = _temp;
  const interval = window.setInterval(() => void check(), POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", onVisibilityChange);
  events.on("routeChangeStart", applyOnNavigation);
  if (pagesRouter) {
    Router.events.on("routeChangeError", recoverFromStaleChunk);
  }
  return () => {
    window.clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    events.off("routeChangeStart", applyOnNavigation);
    Router.events.off("routeChangeError", recoverFromStaleChunk);
  };
}
function _temp(err, url_0) {
  if (url_0 && isStaleChunkError(err)) {
    navigateTo(url_0);
  }
}